import {
  Button,
  Divider,
  Filter,
  type SelectItem,
} from '@vrplatform/components';
import { useTeam } from '@vrplatform/data';
import {
  type MRT_ColumnDef,
  MantineReactTable,
  useLazyTable,
} from '@vrplatform/table';
import { day, formatCurrency, isUUID } from '@vrplatform/utils';
import { Group, Text } from '@mantine/core';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useEmbedViewMode } from '../../providers';
import { TrustReconciliationByListingTableFilter } from './TrustReconciliationByListingTableFilter';
import { getTrustReconciliationByListingTableCellStyle } from './_styles';
import { useTrustReconciliationByListingTableFilter } from './useTrustReconciliationByListingTableFilter';
import {
  type TrustReconciliationByListingTableQueryRow,
  type TrustReconciliationByListingTableQuerySorting,
  useTrustReconciliationByListingTableQuery,
} from './useTrustReconciliationByListingTableQuery';

export type TrustReconciliationByListingJournalEntriesArgs = {
  accountIds: string;
  listingOwnershipPeriodIds: string;
  date: string;
  party: TrustReconciliationByListingTableQueryRow['party'];
};

const getRowAccountIds = (row: TrustReconciliationByListingTableQueryRow) => {
  const ids = new Set<string>();

  const visit = (current: TrustReconciliationByListingTableQueryRow) => {
    if (current.accountId) {
      ids.add(current.accountId);
    }

    current.subRows.forEach(visit);
  };

  visit(row);

  return Array.from(ids);
};

function useSorting() {
  const [sorting, setSorting] =
    useState<TrustReconciliationByListingTableQuerySorting>('name');

  return {
    sorting,
    setSorting,
  };
}

const getDefaultExpandedState = (
  rows: TrustReconciliationByListingTableQueryRow[]
) => {
  const expanded: Record<string, boolean> = {};

  const visit = (row: TrustReconciliationByListingTableQueryRow) => {
    if (!row.subRows.length) return;

    const isAccountGroup = row.subRows.every(
      (subRow) => subRow.type === 'account'
    );

    if (!isAccountGroup) {
      expanded[row.id] = true;
      row.subRows.forEach(visit);
    }
  };

  rows.forEach(visit);

  return expanded;
};

export const TrustReconciliationByListingTable = ({
  onOpenJournalEntries,
}: {
  onOpenJournalEntries?: (
    args: TrustReconciliationByListingJournalEntriesArgs
  ) => void;
}) => {
  const { isEmbed } = useEmbedViewMode();
  const { sorting, setSorting } = useSorting();
  const [{ currency, effectiveStatementStartAt: statementStartAt }] = useTeam();
  const { filter } = useTrustReconciliationByListingTableFilter();

  const queryData = useTrustReconciliationByListingTableQuery(
    sorting || 'name'
  );
  const rows = queryData.data?.rows ?? [];

  const defaultExpandedState = useMemo(
    () => getDefaultExpandedState(rows),
    [rows]
  );
  const filterKey = useMemo(() => JSON.stringify(filter), [filter]);
  const appliedFilterKeyRef = useRef<string | null>(null);

  const dateRange = useMemo(() => {
    const endDate = day(filter.endDate).yyyymmdd();
    const startDate =
      statementStartAt && day(statementStartAt).isValid()
        ? day(statementStartAt).yyyymmdd()
        : endDate;

    if (
      day(endDate).isBefore(day(startDate)) ||
      day(endDate).isSame(day(startDate), 'day')
    ) {
      return `...${endDate}`;
    }

    return `${startDate}...${endDate}`;
  }, [filter.endDate, statementStartAt]);

  const columns = useMemo<
    MRT_ColumnDef<TrustReconciliationByListingTableQueryRow>[]
  >(
    () => [
      {
        header: 'Name',
        accessorKey: 'name',
        grow: true,
        mantineTableBodyCellProps: (props) => ({
          sx: () => ({
            fontWeight:
              props.row.original.type === 'account'
                ? 'normal'
                : props.row.depth === 0
                  ? 'bold'
                  : 500,
          }),
        }),
      },
      {
        header: 'Amount',
        grow: false,
        maxSize: 150,
        mantineTableHeadCellProps: {
          align: 'right',
        },
        mantineTableBodyCellProps: (props) => ({
          align: 'right',
          sx: () => ({
            fontWeight:
              props.row.original.type === 'account' ? 'normal' : 'bold',
          }),
        }),
        accessorFn: (row) => formatCurrency(row.centTotal / 100, currency),
        Cell: ({ row, cell }) => {
          const accountIds = getRowAccountIds(row.original);
          const disabled = isEmbed || !onOpenJournalEntries;

          const onClick = disabled
            ? undefined
            : () => {
                onOpenJournalEntries({
                  accountIds: accountIds.length
                    ? accountIds.join(',')
                    : 'unmapped',
                  listingOwnershipPeriodIds:
                    row.original.listingOwnershipPeriodId &&
                    isUUID(row.original.listingOwnershipPeriodId)
                      ? row.original.listingOwnershipPeriodId
                      : 'unmapped',
                  date: dateRange,
                  party: row.original.party,
                });
              };

          return (
            <Text
              ta="right"
              onClick={onClick}
              sx={
                !disabled
                  ? {
                      cursor: 'pointer',
                      transition: 'text-decoration 0.2s ease-in-out',
                      ':hover': {
                        textDecoration: 'underline',
                      },
                    }
                  : undefined
              }
            >
              {cell.getValue<string>()}
            </Text>
          );
        },
      },
    ],
    [currency, dateRange, isEmbed, onOpenJournalEntries]
  );

  const table = useLazyTable({
    data: {
      error: queryData.error?.message,
      loading: queryData.isLoading,
      rows,
      rowCount: rows.length,
    },
    table: {
      columns,
      hideTopBar: true,
      hidePagination: true,
    },
    subRows: {
      getRowCanExpand: (row) => (row.subRows?.length ?? 0) > 0,
      getSubRows: (row) => row.subRows,
      allowExpandingAll: false,
      columnWidth: 50,
    },
    styles: {
      row: (props, theme) =>
        getTrustReconciliationByListingTableCellStyle(theme, {
          rowDepth: props.row.depth,
          type: props.row.original.type,
        }),
    },
    virtualization: {
      enableRowVirtualization: true,
      overscan: 30,
    },
  });

  useEffect(() => {
    if (!rows.length) return;
    if (appliedFilterKeyRef.current === filterKey) return;

    table.setExpanded(defaultExpandedState);
    appliedFilterKeyRef.current = filterKey;
  }, [defaultExpandedState, filterKey, rows.length, table]);

  return (
    <>
      <Group justify="space-between" align="start">
        <Group>
          <TrustReconciliationByListingTableFilter />
        </Group>

        <Group>
          <Sort sorting={sorting} setSorting={setSorting} />
          <Divider orientation="vertical" />

          <Button
            variant="transparent"
            leftIcon="CollapseIcon"
            leftIconSize={14}
            onClick={() => table.toggleAllRowsExpanded()}
          >
            Collapse all
          </Button>
        </Group>
      </Group>

      <Divider mt="sm" />
      <MantineReactTable table={table} />
    </>
  );
};

const Sort = ({
  sorting,
  setSorting,
}: {
  sorting: TrustReconciliationByListingTableQuerySorting;
  setSorting: (sorting: TrustReconciliationByListingTableQuerySorting) => void;
}) => {
  const options = useMemo<
    SelectItem<TrustReconciliationByListingTableQuerySorting>[]
  >(
    () => [
      {
        label: 'Sort by Name',
        value: 'name',
      },
      {
        label: 'Sort by Amount',
        value: 'centTotal',
      },
    ],
    []
  );

  const value = options.find((option) => option.value === sorting) ?? null;

  return (
    <Filter.Select
      label="Sort by"
      type="single"
      value={value}
      data={{ options }}
      hideClearButton
      hideSearch
      setValue={(value) => value?.value && setSorting(value.value)}
    />
  );
};
